/**
 * Who is signed in, and what they are allowed to see.
 *
 * Deny-by-default: until the manifest has loaded, `can()` answers false. The
 * alternative — assuming permission while a request is in flight — flashes UI
 * a user may not open, and this app gates cash collection and grade entry.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/api/client";
import { setOfflineScope } from "@/offline/db";
import { clearCache, isOfflineError } from "@/offline/readCache";
import { forgetSession, recallSession, rememberSession } from "@/offline/sessionCache";
import type { PermissionCode } from "@/api/permissions";
import {
  fetchCurrentUser, fetchManifest, signIn as apiSignIn, signOut as apiSignOut,
  type CurrentUser, type UiManifest,
} from "./session";

type AuthState = {
  status: "loading" | "authenticated" | "anonymous";
  /** The session was restored from cache — the server has not been reached
   *  since this app was opened. Screens use it to label what they show. */
  offline: boolean;
  user: CurrentUser | null;
  manifest: UiManifest | null;
  /** Typed against the backend catalogue: a code that does not exist is a
   *  compile error, not a screen that silently never appears. */
  can: (code: PermissionCode) => boolean;
  canAny: (codes: PermissionCode[]) => boolean;
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [manifest, setManifest] = useState<UiManifest | null>(null);
  /** True when this session was restored from cache rather than the server. */
  const [offline, setOffline] = useState(false);

  /** Shared by the live and the restored-from-cache paths. */
  const applySession = useCallback((me: CurrentUser, ui: UiManifest) => {
    setUser(me);
    setManifest(ui);
    // Scope offline storage to THIS account. School devices are shared, and
    // the database name carries org+user+role so one person's queued marks
    // can never drain under the next person's session.
    setOfflineScope(
      me.organization_id
        ? { orgId: me.organization_id, userId: me.id, role: me.role ?? "" }
        : null,
    );
  }, []);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      // Both are needed before the shell can render: the user for the header,
      // the manifest for every gate.
      const [me, ui] = await Promise.all([
        fetchCurrentUser(signal),
        fetchManifest(signal),
      ]);
      applySession(me, ui);
      setOffline(false);
      // Remembered so the app can be reopened with no network at all.
      void rememberSession("me", me);
      void rememberSession("ui", ui);
      setStatus("authenticated");
    } catch (error) {
      if (signal?.aborted) return;

      // NO NETWORK is not "signed out". Without this the teacher who closes
      // the app in a courtyard cannot reopen it: the shell comes back from the
      // service worker and then drops to the login screen, while their queued
      // marks sit in IndexedDB waiting for the session it just discarded.
      //
      // Only for connectivity failures — a 401 means the session really is
      // over and must end.
      if (isOfflineError(error)) {
        const [me, ui] = await Promise.all([
          recallSession<CurrentUser>("me"),
          recallSession<UiManifest>("ui"),
        ]);
        if (me && ui) {
          applySession(me, ui);
          setOffline(true);
          setStatus("authenticated");
          return;
        }
      }

      // A 401 here is the normal cold-start answer for a signed-out visitor,
      // not an error worth surfacing.
      if (!(error instanceof ApiError && error.isUnauthenticated)) {
        console.error("session load failed", error);
      }
      setUser(null);
      setManifest(null);
      setStatus("anonymous");
    }
  }, [applySession]);

  useEffect(() => {
    const controller = new AbortController();
    // The lint rule cannot see past an async call, so it reads this as a
    // synchronous setState. It is not: every setState in load() runs after an
    // await. And fetching the session on mount is the case effects exist for —
    // synchronising with an external system — so this one is suppressed rather
    // than restructured. Aborting on unmount is the actual correctness point.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // A session restored from cache must be revalidated as soon as the network
  // is back — otherwise the "Hors ligne" banner stays up on a working
  // connection, which is the same lie in reverse. Observed exactly that: the
  // queue drained (200) and the banner never went away.
  //
  // Only when we are actually in the offline state, so a healthy session does
  // not refetch /me every time a tab is focused. The `online` event is
  // optimistic — if this attempt fails we simply stay offline and try again on
  // the next one.
  useEffect(() => {
    if (!offline) return;
    const revalidate = () => { void load(); };
    window.addEventListener("online", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      window.removeEventListener("online", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [offline, load]);

  const permissions = useMemo(
    () => new Set(manifest?.permissions ?? []),
    [manifest],
  );

  const value = useMemo<AuthState>(() => ({
    status,
    offline,
    user,
    manifest,
    can: (code) => permissions.has(code),
    canAny: (codes) => codes.some((code) => permissions.has(code)),
    signIn: async (identifier, password) => {
      await apiSignIn(identifier, password);
      setStatus("loading");
      await load();
    },
    signOut: async () => {
      setOffline(false);
      await apiSignOut();
      // Cached reads are this user's data; the next person on this device must
      // not find it. The write QUEUE is deliberately left alone — unsent marks
      // belong to whoever made them and drain on their next sign-in.
      await clearCache();
      await forgetSession();
      setOfflineScope(null);
      setUser(null);
      setManifest(null);
      setStatus("anonymous");
    },
  }), [status, offline, user, manifest, permissions, load]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}
